<template>
  <div id="app" class="overflow-hidden feriesystem-app">
    <!-- Navigation Bar Component -->
    <Navbar
      :isLoggedIn="isLoggedIn"
      :user="authStore.user"
      :currentView="currentView"
      @show-registration="() => setView(Views.REGISTRATION)"
      @show-login="() => setView(Views.LOGIN)"
      @select-user="handleUserSelect"
    />

    <div class="mx-auto px-2 2xs:px-4 mb-4 mt-1.5">
      <!-- Conditional Rendering Based on Authentication Status -->
      <template v-if="!isLoggedIn">
        <!-- Login Form Component -->
        <LoginForm
          v-if="isLoginView"
          @show-registration="() => setView(Views.REGISTRATION)"
          class="mt-4"
        />
        <!-- Registration Form Component -->
        <RegistrationForm
          v-else-if="isRegistrationView"
          @registration-success="handleRegistrationSuccess"
          @show-login="() => setView(Views.LOGIN)"
          class="mt-4"
        />
      </template>

      <template v-else>
        <!-- User Dashboard Component -->
        <UserDashboard
          v-if="isUserView"
          :user="selectedUser || authStore.user"
        />
        <!-- Admin Dashboard Component -->
        <AdminDashboard v-else-if="isAdminView" />
      </template>

      <!-- Toast Notifications Container -->
      <div
        class="fixed w-full max-w-md space-y-2 transform -translate-x-1/2 bottom-5 left-1/2"
      >
        <NotificationToast
          v-for="toast in toasts"
          :key="toast.id"
          :message="toast.message"
          :type="toast.type"
          @close="removeToast(toast.id)"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useUsersStore } from "@/stores/usersStore";
import { useAuthStore } from "@/stores/authStore";
import { useRegistrationStore } from "@/stores/registrationStore";
import { useEventsStore } from "@/stores/eventsStore";

import Navbar from "./components/ui/NavBar.vue";
import LoginForm from "./components/pages/LoginForm.vue";
import RegistrationForm from "./components/pages/RegistrationForm.vue";
import UserDashboard from "@/components/pages/UserDashboard.vue";
import AdminDashboard from "@/components/pages/AdminDashboard.vue";
import NotificationToast from "@/components/ui/NotificationToast.vue";

// Enum-like object for views
const Views = {
  LOGIN: "login",
  REGISTRATION: "registration",
  USER: "user",
  ADMIN: "admin",
};

// Initialize stores using Pinia's useStore functions
const usersStore = useUsersStore();
const authStore = useAuthStore();
const registrationStore = useRegistrationStore();
const eventsStore = useEventsStore();

const currentView = ref(Views.LOGIN);

// Toast state
const toasts = ref([]);
const selectedUser = ref(null);

/**
 * Sets the current view based on the authenticated user's role and selection.
 */
const setViewBasedOnUser = async () => {
  if (authStore.user) {
    if (selectedUser.value) {
      currentView.value = Views.USER;
    } else if (authStore.isAdmin) {
      currentView.value = Views.ADMIN;
    } else {
      currentView.value = Views.USER;
    }
  } else {
    currentView.value = Views.LOGIN;
  }
};

/**
 * Handles successful registration by redirecting the user to the login view.
 */
const handleRegistrationSuccess = () => {
  currentView.value = Views.LOGIN;
};

/**
 * Sets the current view and clears the selected user if navigating away from the user view.
 * @param {string} view - The view to navigate to.
 */
const setView = (view) => {
  if (view !== Views.USER) {
    selectedUser.value = null;
  }
  currentView.value = view;
};

/**
 * Adds a toast notification to the toasts array.
 * @param {Object} param0 - The toast object containing message and type.
 */
const addToast = ({ message, type }) => {
  const id = Date.now() + Math.random(); // Improved unique ID generation
  toasts.value.push({ id, message, type });

  // Automatically remove the toast after 10 seconds
  setTimeout(() => {
    removeToast(id);
  }, 10000);
};

/**
 * Removes a toast notification by its ID.
 * @param {number} id - The unique ID of the toast to remove.
 */
const removeToast = (id) => {
  const index = toasts.value.findIndex((toast) => toast.id === id);
  if (index !== -1) {
    toasts.value.splice(index, 1);
  }
};

// Computed properties for view checks
const isLoginView = computed(() => currentView.value === Views.LOGIN);
const isRegistrationView = computed(() => currentView.value === Views.REGISTRATION);
const isUserView = computed(() => currentView.value === Views.USER);
const isAdminView = computed(() => currentView.value === Views.ADMIN);

const isLoggedIn = computed(() => !!authStore.user);

/**
 * Handles user selection by setting the selected user and navigating to the user view.
 * @param {Object} user - The user object that was selected.
 */
const handleUserSelect = (user) => {
  selectedUser.value = user;
  setView(Views.USER);
};

/**
 * Consolidated watcher for displaying toasts based on various store statuses.
 */
const setupStoreWatchers = () => {
  // Watch for authentication errors and successes
  watch(
    () => authStore.status.error,
    (newError) => {
      if (newError) {
        addToast({ message: newError, type: "error" });
      }
    }
  );

  watch(
    () => authStore.status.success,
    (newSuccess) => {
      if (newSuccess) {
        addToast({ message: newSuccess, type: "success" });
      }
    }
  );

  // Watch for registration errors and successes
  watch(
    () => registrationStore.status.error,
    (newError) => {
      if (newError) {
        addToast({ message: newError, type: "error" });
      }
    }
  );

  watch(
    () => registrationStore.status.success,
    (newSuccess) => {
      if (newSuccess) {
        addToast({ message: newSuccess, type: "success" });
      }
    }
  );

  // Watch for eventsStore errors and saved events
  watch(
    () => eventsStore.errors,
    (newErrors) => {
      newErrors.forEach((error) => {
        addToast({ message: error, type: "error" });
      });
    }
  );

  watch(
    () => eventsStore.savedEvents,
    (newSavedEvents) => {
      if (newSavedEvents.length > 0) {
        addToast({ message: "Feriedage gemt", type: "success" });
      }
    }
  );

  // Watch for usersStore errors
  watch(
    () => usersStore.status.error,
    (newError) => {
      if (newError) {
        addToast({ message: newError, type: "error" });
      }
    }
  );
};

/**
 * Watcher to update the view whenever the authenticated user changes.
 * Executes immediately on component mount.
 */
 watch(() => authStore.user, setViewBasedOnUser, { immediate: true });

/**
 * Watcher to update the view whenever the selected user changes.
 * Executes immediately on component mount.
 */
watch(selectedUser, (newSelectedUser) => {
  if (newSelectedUser) {
    setView(Views.USER);
  } else if (authStore.user && authStore.isAdmin) {
    setView(Views.ADMIN);
  }
}, { deep: true });

onMounted(() => {
  setupStoreWatchers();
});
</script>